import { useImageSrcWithFallback } from "../../../../hooks/useImageSrcWithFallback";
import { HTMLAttributes, RefObject, useEffect, useMemo, useState, useCallback, memo, useRef } from "react";
//@ts-ignore
import clubPlaceholder from "ui-kit/assets/images/logo_fixtures.png";
import { calculateEventTime, getTextColorForBackground, handlePlayEvent } from "../../../functions";
import type { EventListTeamInfo, MergedGpEventResponse, VideoPlayerRefContextValue } from "../../../types";
import { VideoSidebar, eventListConfig } from "../../../types";
import "./eventList.css";
import type { MediaPlayerInstance } from "@vidstack/react";
import clsx from "clsx";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { Badge } from "ui-kit";
import { TooltipComponent } from "ui-kit/src/TooltipComponent/TooltipComponent";
import { EventSlider } from "./EventSlider/EventSlider";
import { useGameStore } from "../../../store";
import { useEditStore } from "../../../edit-store";
import { EventItemProgressHolder, calculatePercentage } from "./EventItem/EventItemProgressHolder";
import { MessageSquareIcon } from "lucide-react";
import { ATTACH_CLIPS_MAX } from "../../../attach-mode";

interface EventItemProps extends HTMLAttributes<HTMLDivElement> {
	counter: number;
	mergedEvent: MergedGpEventResponse;
	onSelectEvent: (event: MergedGpEventResponse) => void;
	selectionAllowed?: boolean;
	selectionDisabled?: boolean;
	isSelected: boolean;
	containerRef: RefObject<HTMLDivElement>;
	mergedEvents: MergedGpEventResponse[];
	playerRef: RefObject<MediaPlayerInstance> | null;
	isClip?: boolean;
	teamInfo: EventListTeamInfo;
	currentTime: number;
	hasProgress: boolean;
	eventCommentsPermission: boolean;
	onOpenComments: (event: MergedGpEventResponse) => void;
}

export const EventItem = memo(function EventItem({
	teamInfo,
	containerRef,
	counter,
	mergedEvent,
	onSelectEvent,
	selectionAllowed,
	selectionDisabled = false,
	isSelected,
	mergedEvents,
	playerRef,
	isClip,
	currentTime,
	hasProgress,
	eventCommentsPermission,
	onOpenComments
}: EventItemProps) {
	const [inEdit, setInEdit] = useState<boolean>(false);
	const [editValue, setEditValue] = useState<number[]>([mergedEvent.startTime, mergedEvent.endTime]);

	const { setEditEvent } = useEditStore();
	const { setSidebar, followVideo, setFollowVideo, setMergedEvents } = useGameStore();

	const eventData = useMemo(() => {
		const isHighlight = mergedEvent.types.includes("highlight") && mergedEvent.types.length === 1;
		const isHomeTeam = teamInfo.homeTeam.id === mergedEvent.gameTeamId;
		const shirtColor = mergedEvent.teamShirtColor ?? (isHomeTeam ? teamInfo.homeTeam.shirtColor : teamInfo.awayTeam.shirtColor);
		const teamLogo = mergedEvent.teamClubLogo ?? (isHomeTeam ? teamInfo.homeTeam.clubLogo : teamInfo.awayTeam.clubLogo);
		const prependLeft = mergedEvent.videoTime - eventListConfig.prepend.max * 1000;
		const prependRight = mergedEvent.videoTime - eventListConfig.prepend.min * 1000;
		const appendLeft = mergedEvent.lastVideoTime + eventListConfig.append.min * 1000;
		const appendRight = mergedEvent.lastVideoTime + eventListConfig.append.max * 1000;
		const prependMax = eventListConfig.prepend.max;
		const appendMax = eventListConfig.append.max;

		return {
			isHighlight,
			isHomeTeam,
			shirtColor,
			teamLogo,
			prependLeft,
			prependRight,
			appendLeft,
			appendRight,
			prependMax,
			appendMax
		};
	}, [mergedEvent, teamInfo]);

	const { imgSrc: eventTeamLogoSrc, onImgError: onEventTeamLogoError } = useImageSrcWithFallback({
		sourceKey: eventData.teamLogo,
		fallbackSrc: clubPlaceholder,
	});

	const handleEventClick = useCallback((playerRef: VideoPlayerRefContextValue, event: MergedGpEventResponse) => {
		setFollowVideo(false);
		handlePlayEvent(playerRef, event.startTime);
		setTimeout(() => {
			setFollowVideo(true);
		}, 300);
	}, [setFollowVideo]);

	const calculatePrepend = useCallback((time: number): number => {
		return eventData.prependMax - Math.round((time - eventData.prependLeft) / 1000);
	}, [eventData.prependMax, eventData.prependLeft]);

	const calculateAppend = useCallback((time: number): number => {
		return eventData.appendMax - Math.round((eventData.appendRight - time) / 1000);
	}, [eventData.appendMax, eventData.appendRight]);

	const handleConfirmUpdateEvent = useCallback((): void => {
		const prevMergedEvents = [...mergedEvents];

		prevMergedEvents[counter - 1].prependSeconds = calculatePrepend(editValue[0]);
		prevMergedEvents[counter - 1].appendSeconds = calculateAppend(editValue[1]);
		prevMergedEvents[counter - 1].startTime = prevMergedEvents[counter - 1].videoTime - prevMergedEvents[counter - 1].prependSeconds * 1000;
		prevMergedEvents[counter - 1].endTime = prevMergedEvents[counter - 1].lastVideoTime + prevMergedEvents[counter - 1].appendSeconds * 1000;
		prevMergedEvents[counter - 1].isMutated =
			prevMergedEvents[counter - 1].prependSeconds !== eventListConfig.prepend.default ||
			prevMergedEvents[counter - 1].appendSeconds !== eventListConfig.append.default;

		setInEdit(false);
		setMergedEvents(prevMergedEvents);

		handlePlayEvent(playerRef, prevMergedEvents[counter - 1].startTime);
	}, [mergedEvents, counter, editValue, calculatePrepend, calculateAppend, setMergedEvents, playerRef]);

	const handleCancelUpdateEvent = useCallback((): void => {
		setInEdit(false);
		setEditValue([mergedEvent.startTime, mergedEvent.endTime]);
		handlePlayEvent(playerRef, mergedEvent.startTime);
	}, [mergedEvent.startTime, mergedEvent.endTime, playerRef]);

	const handleOnEdit = useCallback(() => {
		setInEdit(true);
		if (playerRef?.current) {
			playerRef.current.pause();
		}
	}, [playerRef]);

	const handleEditHighlight = useCallback(() => {
		setEditEvent(mergedEvent);
		setSidebar(VideoSidebar.EditEvent);
		if (playerRef?.current) {
			playerRef.current.pause();
		}
	}, [mergedEvent, setEditEvent, setSidebar, playerRef]);

	const onCommentClicked = () => {
		onOpenComments(mergedEvent);
	};

	const onSelectEventCallback = () => {
		onSelectEvent(mergedEvent);
	};

	const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	
	useEffect(() => {
		if (!hasProgress || !followVideo) return;
		
		if (scrollTimeoutRef.current) {
			clearTimeout(scrollTimeoutRef.current);
		}
		
		scrollTimeoutRef.current = setTimeout(() => {
			const container = containerRef.current;
			if (container) {
				const activeElements = container.querySelectorAll(".activeEvent");
				if (activeElements.length > 0) {
					const lastElement = activeElements[activeElements.length - 1] as HTMLElement;
					const containerRect = container.getBoundingClientRect();
					const elementRect = lastElement.getBoundingClientRect();

					const offsetTop = elementRect.top - containerRect.top;
					const newScrollTop = container.scrollTop + offsetTop - container.clientHeight / 2 + elementRect.height / 2;

					container.scrollTo({ top: newScrollTop, behavior: "auto" });
				}
			}
		}, 16);
		
		return () => {
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}
		};
	}, [hasProgress, followVideo, containerRef]);

	const onSliderChange = useCallback((val: number[]) => {
		const { prependLeft, prependRight, appendLeft, appendRight } = eventData;
		
		if (val[0] < prependLeft) val[0] = prependLeft;
		if (val[0] > prependRight) val[0] = prependRight;
		if (val[1] < appendLeft) val[1] = appendLeft;
		if (val[1] > appendRight) val[1] = appendRight;

		if (playerRef?.current) {
			if (val[0] !== editValue[0]) {
				playerRef.current.currentTime = val[0] / 1000;
			}
			if (val[1] !== editValue[1]) {
				playerRef.current.currentTime = val[1] / 1000;
			}
		}

		setEditValue(val);
	}, [eventData, editValue, playerRef]);

	const width = !hasProgress ? 0 : calculatePercentage(mergedEvent, currentTime, eventData.prependMax, eventData.appendMax);

	const sequenceLength = Math.round((editValue[1] - editValue[0]) / 1000);

	const eventTime = calculateEventTime(mergedEvent);

	const shirtStyle = useMemo(() => ({
		color: getTextColorForBackground(eventData.shirtColor ?? "white"),
		backgroundColor: eventData.shirtColor ?? "white"
	}), [eventData.shirtColor]);

	const hitElements = useMemo(() => 
		mergedEvent.hits.map((hitTime, hitIndex) => (
			<div
				className="eventItem__hit"
				style={{ left: `${calculatePercentage(mergedEvent, hitTime, eventData.prependMax, eventData.appendMax, true)}%` }}
				key={`${hitIndex}-${hitTime}`}
			/>
		)), 
		[mergedEvent.hits, mergedEvent, eventData.prependMax, eventData.appendMax]
	);

	const typesBadges = useMemo(() => 
		!eventData.isHighlight ? mergedEvent.types.map((type) => (
			<Badge
				variant="default"
				key={`${mergedEvent.id}-${type}`}
				label={`#${_(`event.v3.type.${type}` as keyof Keys).toLowerCase()}`}
				outline={true}
				size="xs"
			/>
		)) : null,
		[eventData.isHighlight, mergedEvent.types, mergedEvent.id]
	);

	return (
		<>
			<div
				className={clsx("eventItem", { 
					activeEvent: hasProgress, 
					"eventItem--mutated": mergedEvent.isMutated, 
					inEdit 
				})}
				data-in-edit={inEdit}
				data-start-time={mergedEvent.startTime}
				data-end-time={mergedEvent.endTime}
			>
				<div className="eventItem__content" onClick={() => handleEventClick(playerRef, mergedEvent)}>
					{!eventData.isHighlight && (
						<div className="eventItem__content__detail">
							<div className="eventItem__content__shirt" style={shirtStyle}>
								{mergedEvent.backNumber}
							</div>
							<div className="eventItem__content__club">
								<img src={eventTeamLogoSrc ?? clubPlaceholder} alt="logo" onError={onEventTeamLogoError} />
							</div>
						</div>
					)}
					<div className="eventItem__content__length">{sequenceLength}s</div>
					<div className="eventItem__content__body">
						{eventTime}
						{eventData.isHighlight && ` - ${mergedEvent.details?.title}`}
						{!eventData.isHighlight && mergedEvent.playerName && ` - ${mergedEvent.playerName}`}
						<div className="eventItem__types">
							{eventData.isHighlight && `${mergedEvent.details?.note}`}
							{typesBadges}
						</div>
						{!inEdit && (
							<EventItemProgressHolder
								appendMax={eventData.appendMax}
								prependMax={eventData.prependMax}
								isHighlight={eventData.isHighlight}
								mergedEvent={mergedEvent}
								width={width}
								playerRef={playerRef}
							/>
						)}
					</div>
				</div>
				{selectionAllowed &&
					(selectionDisabled ? (
						<span className="eventItem__checkbox-tooltip">
							<TooltipComponent
								title={_("game.attachMode.selection.maxReachedTooltip", { max: String(ATTACH_CLIPS_MAX) })}
								variant="dark"
								delay={250}
								size="large"
							>
								<span>
									<input type="checkbox" checked={false} disabled readOnly />
								</span>
							</TooltipComponent>
						</span>
					) : (
						<input
							type="checkbox"
							checked={isSelected}
							onClick={(e) => e.stopPropagation()}
							onChange={onSelectEventCallback}
						/>
					))}
				{!(inEdit || isClip) && (
					<div onClick={handleOnEdit} className="eventItem__edit-handle">
						<span className="icon-new icon-scissors icon-blue icon-14" />
					</div>
				)}
				{eventData.isHighlight && !isClip && !inEdit && (
					<div className="eventItem__editHighlight" onClick={handleEditHighlight}>
						<span className="icon-new icon-crayon" />
					</div>
				)}
				{inEdit && (
					<div className="eventItem__editable">
						<div className="eventItem__editable__slider">
							<EventSlider
								className="react-aria-Slider event-slider"
								value={editValue}
								onChange={onSliderChange}
								minValue={eventData.prependLeft}
								maxValue={eventData.appendRight}
								step={1000}
							/>
							{hitElements}
						</div>
						<div className="eventItem__editable__prepend">-{calculatePrepend(editValue[0])}s</div>
						<div className="eventItem__editable__append">+{calculateAppend(editValue[1])}s</div>
						<Badge
							variant="danger"
							size="sm"
							label={_("gamePage.eventEdit.cancel.label")}
							onClick={handleCancelUpdateEvent}
							outline={true}
							className="eventItem__editable__button"
						/>
						<Badge
							variant="primary"
							size="sm"
							label={_("gamePage.eventEdit.confirm.label")}
							onClick={handleConfirmUpdateEvent}
							className="eventItem__editable__button"
						/>
					</div>
				)}
			</div>
			{eventCommentsPermission && (
				<div className="form-footer text-center">
					<button 
						className="w-full mb-3 bg-gray-100 hover:bg-gray-50 text-gray-800 font-bold py-2 px-4 rounded-l flex justify-center items-center relative" 
						onClick={onCommentClicked}
					>
						{mergedEvent.comments && mergedEvent.comments.length ? (
							<>
								<div className="flex items-center justify-center">
									<MessageSquareIcon className="w-3 h-3 text-blue-500 mr-2" />
									{_("gamePage.event.seeComments.label")}
								</div>
								<span className="absolute right-4 flex items-center justify-center w-5 h-5 text-xs rounded-full" style={{ backgroundColor: '#03C5FF' }}>
									{mergedEvent.comments.length}
								</span>
							</>
						) : (
							<>
								<MessageSquareIcon className="w-3 h-3 text-blue-500 mr-2" />
								<span>{_("gamePage.event.comment.label")}</span>
							</>
						)}
					</button>
				</div>
			)}

		</>
	);
});
